import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import withApi from '../../components/higherOrderComponents/withApi';
import { readQuery } from '../query';
import ListTools from '../components/ListTools';
import ActiveFilters from '../components/ActiveFilters';
import EmptyRow from '../components/EmptyRow';
import Guid from '../components/Guid';
import Pager from '../components/Pager';
import StatusPill from '../components/StatusPill';
import LookupSelect from '../components/LookupSelect';
import { lookupProduct, lookupUser } from '../lookups';

/**
 * Purchases — the searchable list.
 *
 * All seven filters of the old page, and its seven columns. The status options
 * come from `enums/purchaseStatus`, read rather than restated so the two cannot
 * drift: a status this page offers but the API does not know would return
 * nothing and look like an empty result.
 *
 * `searchPurchase` is a POST that answers a bare array with no total, so the
 * pager offers a next page whenever the current one came back full — the same
 * guess the old admin makes.
 */

import { purchaseStatus } from '../../enums/purchaseStatus';

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const money = value => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : `${n.toFixed(2)} DKK`;
};

// User and product take ids, and are `LookupSelect`s below: this page is where
// somebody lands from "what did this person buy", and pasting a GUID they do not
// have is not an answer.
/**
 * What `PurchasesController` can sort by.
 *
 * `total` is the one worth having: a list of purchases nobody can order by
 * amount cannot answer "what were the largest transactions this week", which is
 * a question this screen gets opened for.
 *
 * "Occurs at" is not sortable and does not pretend to be. The endpoint computes
 * it per row from the earliest of a purchase's products, in memory; ordering by
 * it in SQL would mean a different definition than the column shows, and two
 * definitions of one field is how a sort starts lying.
 */
const SORTABLE = {
  Created: 'created',
  Total: 'total',
  Status: 'status',
  Reference: 'reference'
};

const FILTERS = [
  { name: 'purchaseId', label: 'Purchase id', placeholder: 'Find by id' },
  { name: 'reference', label: 'Reference', placeholder: 'Filter by reference' },
  { name: 'afterAt', label: 'After', type: 'datetime-local' },
  { name: 'beforeAt', label: 'Before', type: 'datetime-local' }
];

const EXPORT_COLUMNS = [
  ['Id', 'Id'],
  ['Status', 'Status'],
  ['CreatedAtCet', 'Created (CET)'],
  ['UserId', 'User id'],
  ['UserName', 'User'],
  ['UserEmail', 'Email'],
  ['TotalAmount', 'Total'],
  ['CurrencyIsoCode', 'Currency'],
  ['ProductNames', 'Products']
];

const FILTER_FIELDS = FILTERS;

const PurchasesInternal = ({ api }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(() => readQuery(location.search), [location.search]);

  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState(query);

  const page = parseInt(query.page, 10) || 1;
  // The same question the chips answer, so the empty row and the chips
  // can never disagree about whether anything is filtering the list.
  const orderBy = query.orderBy || SORTABLE.Created;
  const orderDirection = query.orderDirection === 'asc' ? 'asc' : 'desc';

  const filtered = FILTER_FIELDS.some(({ name: key }) => (
    key !== 'page' && key !== 'resultsPerPage' && query[key] !== undefined && query[key] !== ''
  ));

  const perPage = parseInt(query.resultsPerPage, 10) || 15;

  useEffect(() => {
    setDraft(query);
  }, [query]);

  const load = useCallback(async () => {
    setRows(null);
    setFailed(false);
    try {
      const data = await api.purchases.searchPurchase({
        filters: query,
        skipPages: page,
        resultsCount: perPage
      });
      setRows(Array.isArray(data) ? data : (data?.Purchases ?? []));
    } catch (error) {
      setFailed(true);
    }
  }, [api, query, page]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = (next, { resetPage = true } = {}) => {
    // The sort survives a search. It is not a filter — the filter row does not
    // hold it and `next` is built from that row — so applying one used to drop
    // the column somebody had just sorted by, silently, back to the default.
    const merged = {
      orderBy: query.orderBy,
      orderDirection: query.orderDirection,
      ...next
    };
    if (resetPage) delete merged.page;

    const search = Object.entries(merged)
      .filter(([, value]) => value !== '' && value != null)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    navigate(search ? `?${search}` : '');
  };

  const set = (name, value) => setDraft(previous => ({ ...previous, [name]: value }));

  const sortBy = key => apply({
    ...query,
    orderBy: key,
    orderDirection: orderBy.toLowerCase() === key.toLowerCase() && orderDirection === 'desc' ? 'asc' : 'desc'
  });

  const sortMark = key =>
    (orderBy.toLowerCase() === key.toLowerCase() ? (orderDirection === 'desc' ? ' ↓' : ' ↑') : '');

  const header = (label, key, className) => (
    <th className={className}>
      {key ? (
        <button type="button" className="next-th-sort" onClick={() => sortBy(key)}>
          {label}{sortMark(key)}
        </button>
      ) : label}
    </th>
  );

  return (
    <div className="next-page next-page--wide">
      <header className="next-page__header next-page__header--row">
        <div>
          <h1>Purchases</h1>
          <p className="next-muted">
            {rows === null
              ? 'Loading…'
              : `Page ${page} · ${rows.length} row${rows.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="next-page__actions">
          <button type="button" className="next-btn" onClick={load}>Refresh</button>
        </div>
      </header>

      <section className="next-card">
        <form
          className="next-filters"
          onSubmit={event => { event.preventDefault(); apply(draft); }}
        >
          <div className="next-filters__row">
            {FILTERS.map(({ name, label, placeholder, type }) => (
              <label className="next-filter" key={name}>
                <span className="next-filter__label">{label}</span>
                <input
                  className="next-input"
                  type={type || 'text'}
                  value={draft[name] ?? ''}
                  placeholder={placeholder}
                  onChange={event => set(name, event.target.value)}
                />
              </label>
            ))}

            <LookupSelect
              label="User"
              value={draft.userId}
              onChange={value => set('userId', value)}
              loadOptions={key => lookupUser(api, key)}
              placeholder="Email or id"
            />

            {/* No type: this page filters by any product, not by memberships. */}
            <LookupSelect
              label="Product"
              value={draft.productId}
              onChange={value => set('productId', value)}
              loadOptions={key => lookupProduct(api, key)}
              placeholder="Name or id"
            />

            <label className="next-filter">
              <span className="next-filter__label">Status</span>
              <select
                className="next-input"
                value={draft.status ?? ''}
                onChange={event => set('status', event.target.value)}
              >
                <option value="">Any</option>
                {purchaseStatus.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>

            <div className="next-filters__buttons">
              <button type="submit" className="next-btn next-btn--primary">Search</button>
              <button
                type="button"
                className="next-btn next-btn--quiet"
                onClick={() => { setDraft({}); apply({}); }}
              >
                Clear
              </button>
            </div>
          </div>
        </form>
      </section>

      <ActiveFilters
        fields={FILTER_FIELDS}
        query={query}
        onChange={next => { setDraft(next); apply(next); }}
      />

      <ListTools
        screenPath="/next/purchases"
        search={location.search}
        columns={EXPORT_COLUMNS}
        rows={rows}
        exportName="purchases"
        perPage={perPage}
        fetchPage={next => api.purchases
          .searchPurchase({ filters: query, skipPages: next, resultsCount: perPage })
          .then(data => (Array.isArray(data) ? data : (data?.Purchases ?? [])))}
      />

      {failed ? (
        <div className="next-note next-note--error">
          Could not load the purchases.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      ) : (
        <section className="next-card">
          <div className="next-table-wrap">
            <table className="next-table next-table--wide next-table--stack">
              <thead>
                <tr>
                  <th>Id</th>
                  {header('Reference', SORTABLE.Reference)}
                  <th>Text</th>
                  {header('Status', SORTABLE.Status)}
                  {header('Total', SORTABLE.Total, 'next-table__right')}
                  {header('Created at (CET)', SORTABLE.Created)}
                  {/* Computed per row from the earliest product, so it cannot be
                      sorted on without changing what it means. */}
                  <th>Occurs at (CET)</th>
                </tr>
              </thead>
              <tbody>
                {rows === null && (
                  <tr><td colSpan={7} className="next-muted next-table__note">Loading…</td></tr>
                )}
                {rows !== null && rows.length === 0 && (
                  <EmptyRow
                    columns={7}
                    noun="purchase"
                    filtered={filtered}
                    onClear={() => { setDraft({}); apply({}); }}
                  />
                )}
                {(rows ?? []).map(row => (
                  <tr key={row.Id}>
                    <td data-label=""><Guid value={row.Id} to={`/next/purchase/${row.Id}`} /></td>
                    <td className="next-nowrap" data-label="Reference">{row.Reference || '—'}</td>
                    <td data-label="Text">{row.Text || '—'}</td>
                    <td data-label="Status"><StatusPill status={row.Status}>{row.Status}</StatusPill></td>
                    <td className="next-nowrap next-table__right" data-label="Total">{money(row.Total ?? row.GrandTotal)}</td>
                    <td className="next-nowrap next-muted" data-label="Created (CET)">{fmtDate(row.CreatedAtCet)}</td>
                    <td className="next-nowrap" data-label="Occurs (CET)">{fmtDate(row.OccursAtCet)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager
            perPage={perPage}
            onPerPageChange={size => apply({ ...query, resultsPerPage: size })}
            page={page}
            hasNext={(rows ?? []).length >= perPage}
            onChange={next => apply({ ...query, page: next }, { resetPage: false })}
          />
        </section>
      )}
    </div>
  );
};

PurchasesInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Purchases = withApi(PurchasesInternal);

Purchases.requiresLogin = true;

export default Purchases;
